import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Store, MapPin, Star, Phone, Mail, Globe, ImagePlus, X, Edit2, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VendorsVenuesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [vendors, setVendors] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showVenueDialog, setShowVenueDialog] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  
  // Sorting and search state
  const [vendorSortOrder, setVendorSortOrder] = useState('asc');
  const [venueSortOrder, setVenueSortOrder] = useState('asc');
  const [vendorSearch, setVendorSearch] = useState('');
  const [venueSearch, setVenueSearch] = useState('');
  
  const [newVendor, setNewVendor] = useState({
    name: '',
    category: 'Lighting',
    contact_name: '',
    phone: '',
    email: '',
    website: '',
    services: [],
    rating: '',
    notes: '',
    logo: ''
  });
  
  const [newVenue, setNewVenue] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    venue_type: '',
    capacity: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    parking_info: '',
    setup_notes: '',
    restrictions: '',
    image: ''
  });
  
  const [serviceInput, setServiceInput] = useState('');
  const [vendorLogoPreview, setVendorLogoPreview] = useState('');
  const [venueImagePreview, setVenueImagePreview] = useState('');

  useEffect(() => {
    fetchVendors();
    fetchVenues();
    fetchVenueTypes();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
    }
  };

  const fetchVenueTypes = async () => {
    try {
      const response = await axios.get(`${API}/venue-types`);
      setVenueTypes(response.data);
      // Set default venue type if available
      if (response.data.length > 0 && !newVenue.venue_type) {
        setNewVenue(prev => ({ ...prev, venue_type: response.data[0].name }));
      }
    } catch (error) {
      console.error('Failed to fetch venue types:', error);
    }
  };

  const handleVendorLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1 * 1024 * 1024) {
        toast.error('Logo size should be less than 1MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setNewVendor({ ...newVendor, logo: base64String });
        setVendorLogoPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVenueImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setNewVenue({ ...newVenue, image: base64String });
        setVenueImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeVenueImage = () => {
    setNewVenue({ ...newVenue, image: '' });
    setVenueImagePreview('');
  };

  const handleCreateVendor = async () => {
    if (!newVendor.name || !newVendor.category) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      // Prepare vendor data - convert empty strings to null and parse rating as float
      const vendorData = {
        ...newVendor,
        rating: newVendor.rating ? parseFloat(newVendor.rating) : null,
        contact_name: newVendor.contact_name || null,
        phone: newVendor.phone || null,
        email: newVendor.email || null,
        website: newVendor.website || null,
        notes: newVendor.notes || null,
        logo: newVendor.logo || null
      };
      
      if (editingVendor) {
        // Update existing vendor
        await axios.put(`${API}/vendors/${editingVendor.id}`, vendorData);
        toast.success('Vendor updated successfully');
      } else {
        // Create new vendor
        await axios.post(`${API}/vendors`, vendorData);
        toast.success('Vendor added successfully');
      }
      
      setShowVendorDialog(false);
      setEditingVendor(null);
      setNewVendor({
        name: '',
        category: 'Lighting',
        contact_name: '',
        phone: '',
        email: '',
        website: '',
        services: [],
        rating: '',
        notes: '',
        logo: ''
      });
      setVendorLogoPreview('');
      fetchVendors();
    } catch (error) {
      toast.error(editingVendor ? 'Failed to update vendor' : 'Failed to add vendor');
    }
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setNewVendor({
      name: vendor.name || '',
      category: vendor.category || 'Lighting',
      contact_name: vendor.contact_name || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      website: vendor.website || '',
      services: vendor.services || [],
      rating: vendor.rating || '',
      notes: vendor.notes || '',
      logo: vendor.logo || ''
    });
    setVendorLogoPreview(vendor.logo || '');
    setShowVendorDialog(true);
  };

  const resetVendorForm = () => {
    setEditingVendor(null);
    setNewVendor({
      name: '',
      category: 'Lighting',
      contact_name: '',
      phone: '',
      email: '',
      website: '',
      services: [],
      rating: '',
      notes: '',
      logo: ''
    });
    setVendorLogoPreview('');
  };

  const handleCreateVenue = async () => {
    if (!newVenue.name || !newVenue.venue_type) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      // Prepare venue data - convert empty strings to null and parse capacity as int
      const venueData = {
        ...newVenue,
        capacity: newVenue.capacity ? parseInt(newVenue.capacity, 10) : null,
        address: newVenue.address || null,
        city: newVenue.city || null,
        state: newVenue.state || null,
        zip_code: newVenue.zip_code || null,
        contact_name: newVenue.contact_name || null,
        contact_phone: newVenue.contact_phone || null,
        contact_email: newVenue.contact_email || null,
        parking_info: newVenue.parking_info || null,
        setup_notes: newVenue.setup_notes || null,
        restrictions: newVenue.restrictions || null,
        image: newVenue.image || null
      };
      await axios.post(`${API}/venues`, venueData);
      toast.success('Venue added successfully');
      setShowVenueDialog(false);
      setNewVenue({
        name: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        venue_type: 'Ballroom',
        capacity: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        parking_info: '',
        setup_notes: '',
        restrictions: '',
        image: ''
      });
      setVenueImagePreview('');
      fetchVenues();
    } catch (error) {
      toast.error('Failed to add venue');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await axios.delete(`${API}/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const handleDeleteVenue = async (venueId) => {
    if (!window.confirm('Are you sure you want to delete this venue?')) return;

    try {
      await axios.delete(`${API}/venues/${venueId}`);
      toast.success('Venue deleted');
      fetchVenues();
    } catch (error) {
      toast.error('Failed to delete venue');
    }
  };

  const handleEditVenue = (venue) => {
    setEditingVenue(venue);
    setNewVenue({
      name: venue.name || '',
      address: venue.address || '',
      city: venue.city || '',
      state: venue.state || '',
      zip_code: venue.zip_code || '',
      venue_type: venue.venue_type || '',
      capacity: venue.capacity || '',
      contact_name: venue.contact_name || '',
      contact_phone: venue.contact_phone || '',
      contact_email: venue.contact_email || '',
      parking_info: venue.parking_info || '',
      setup_notes: venue.setup_notes || '',
      restrictions: venue.restrictions || '',
      image: venue.image || ''
    });
    setVenueImagePreview(venue.image || '');
    setShowVenueDialog(true);
  };

  const handleUpdateVenue = async () => {
    if (!newVenue.name) {
      toast.error('Please enter venue name');
      return;
    }

    try {
      const venueData = {
        ...newVenue,
        capacity: newVenue.capacity ? parseInt(newVenue.capacity) : null
      };
      await axios.put(`${API}/venues/${editingVenue.id}`, venueData);
      toast.success('Venue updated successfully');
      setShowVenueDialog(false);
      resetVenueForm();
      fetchVenues();
    } catch (error) {
      toast.error('Failed to update venue');
    }
  };

  const resetVenueForm = () => {
    setEditingVenue(null);
    setNewVenue({
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      venue_type: venueTypes.length > 0 ? venueTypes[0].name : '',
      capacity: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      parking_info: '',
      setup_notes: '',
      restrictions: '',
      image: ''
    });
    setVenueImagePreview('');
  };

  const addService = () => {
    if (serviceInput.trim() && !newVendor.services.includes(serviceInput.trim())) {
      setNewVendor({
        ...newVendor,
        services: [...newVendor.services, serviceInput.trim()]
      });
      setServiceInput('');
    }
  };

  const removeService = (service) => {
    setNewVendor({
      ...newVendor,
      services: newVendor.services.filter(s => s !== service)
    });
  };

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div className="p-4 sm:p-6" data-testid="vendors-venues-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-4xl font-bold mb-2" data-testid="vendors-venues-heading">Vendors & Venues</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Manage your partner vendors and event venues</p>
        </div>

        <Tabs defaultValue="vendors" className="space-y-6">
          <TabsList className="glass-card border-glass-border">
            <TabsTrigger value="vendors" data-testid="tab-vendors">
              <Store className="w-4 h-4 mr-2" />
                Vendors
              </TabsTrigger>
              <TabsTrigger value="venues" data-testid="tab-venues">
                <MapPin className="w-4 h-4 mr-2" />
                Venues
              </TabsTrigger>
            </TabsList>

            {/* Vendors Tab */}
            <TabsContent value="vendors">
              <div className="flex justify-between items-center mb-6">
                <p className="text-muted-foreground">Track partner vendors and suppliers</p>
                <Dialog open={showVendorDialog} onOpenChange={(open) => { setShowVendorDialog(open); if (!open) resetVendorForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="create-vendor-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Vendor
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-vendor-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl text-foreground">
                        {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Vendor Name *</Label>
                          <Input
                            value={newVendor.name}
                            onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="vendor-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Category *</Label>
                          <Select value={newVendor.category} onValueChange={(v) => setNewVendor({ ...newVendor, category: v })}>
                            <SelectTrigger className="bg-background border-border text-foreground" data-testid="vendor-category-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="Lighting">Lighting</SelectItem>
                              <SelectItem value="Catering">Catering</SelectItem>
                              <SelectItem value="Photography">Photography</SelectItem>
                              <SelectItem value="Videography">Videography</SelectItem>
                              <SelectItem value="Entertainment">Entertainment</SelectItem>
                              <SelectItem value="Equipment Rental">Equipment Rental</SelectItem>
                              <SelectItem value="Floral">Floral</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Vendor Logo</Label>
                        <div className="flex items-center gap-4">
                          {vendorLogoPreview && (
                            <div className="w-20 h-20 rounded-lg overflow-hidden border border-border bg-muted flex items-center justify-center">
                              <img src={vendorLogoPreview} alt="Preview" className="max-w-full max-h-full object-contain p-1" />
                            </div>
                          )}
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleVendorLogoUpload}
                            className="bg-background border-border flex-1"
                            data-testid="vendor-logo-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Contact Name</Label>
                          <Input
                            value={newVendor.contact_name}
                            onChange={(e) => setNewVendor({ ...newVendor, contact_name: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="vendor-contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Phone</Label>
                          <Input
                            value={newVendor.phone}
                            onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="vendor-phone-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Email</Label>
                          <Input
                            type="email"
                            value={newVendor.email}
                            onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="vendor-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Website</Label>
                          <Input
                            value={newVendor.website}
                            onChange={(e) => setNewVendor({ ...newVendor, website: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="vendor-website-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Services Offered</Label>
                        <div className="flex gap-2">
                          <Input
                            value={serviceInput}
                            onChange={(e) => setServiceInput(e.target.value)}
                            placeholder="e.g., LED uplighting"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                            className="bg-background border-border text-foreground"
                            data-testid="vendor-service-input"
                          />
                          <Button onClick={addService} type="button" data-testid="add-service-btn">
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {newVendor.services.map((service, index) => (
                            <div
                              key={index}
                              className="bg-primary/20 text-primary px-3 py-1 rounded-full text-sm flex items-center gap-2"
                              data-testid={`service-tag-${index}`}
                            >
                              {service}
                              <button
                                onClick={() => removeService(service)}
                                className="hover:text-primary/70"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Rating (1-5)</Label>
                          <Input
                            type="number"
                            min="1"
                            max="5"
                            step="0.5"
                            value={newVendor.rating}
                            onChange={(e) => setNewVendor({ ...newVendor, rating: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="vendor-rating-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Notes</Label>
                        <textarea
                          value={newVendor.notes}
                          onChange={(e) => setNewVendor({ ...newVendor, notes: e.target.value })}
                          className="w-full min-h-[80px] bg-background border border-border rounded-lg p-3 text-foreground"
                          data-testid="vendor-notes-input"
                        />
                      </div>
                      <Button
                        onClick={handleCreateVendor}
                        className="w-full bg-neon-blue hover:bg-neon-blue/80 text-black"
                        data-testid="vendor-create-submit-btn"
                      >
                        {editingVendor ? 'Save Changes' : 'Add Vendor'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Vendor Search and Sort */}
              <div className="flex gap-2 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search vendors..."
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                    className="pl-10 bg-card border-border"
                    data-testid="vendor-search-input"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setVendorSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className="border-border flex items-center gap-2 whitespace-nowrap"
                  data-testid="sort-vendors-btn"
                >
                  {vendorSortOrder === 'asc' ? (
                    <ArrowUp className="w-4 h-4" />
                  ) : (
                    <ArrowDown className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Name {vendorSortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-12">Loading...</div>
              ) : vendors.length === 0 ? (
                <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-vendors-message">
                  <p className="text-muted-foreground">No vendors yet. Add your first vendor!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vendors-list">
                  {vendors
                    .filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase()))
                    .sort((a, b) => {
                      const nameA = (a.name || '').toLowerCase();
                      const nameB = (b.name || '').toLowerCase();
                      return vendorSortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
                    })
                    .map((vendor) => (
                    <div
                      key={vendor.id}
                      className="glass-card-hover rounded-2xl overflow-hidden relative"
                      data-testid={`vendor-item-${vendor.id}`}
                    >
                      {vendor.logo && (
                        <div className="h-32 bg-white/5 flex items-center justify-center p-4">
                          <img 
                            src={vendor.logo} 
                            alt={vendor.name}
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="absolute top-4 right-4 flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditVendor(vendor)}
                            className="text-neon-blue hover:text-neon-blue/80 bg-black/50 backdrop-blur"
                            data-testid={`edit-vendor-btn-${vendor.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteVendor(vendor.id)}
                            className="text-red-500 hover:text-red-400 bg-black/50 backdrop-blur"
                            data-testid={`delete-vendor-btn-${vendor.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-neon-blue font-semibold uppercase">{vendor.category}</span>
                            {vendor.added_by_client && (
                              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                                Client Added
                              </span>
                            )}
                          </div>
                          <h3 className="font-heading text-xl font-bold mt-1">{vendor.name}</h3>
                          {vendor.added_by_client && vendor.added_by_client_name && (
                            <p className="text-xs text-muted-foreground mt-1">Added by: {vendor.added_by_client_name}</p>
                          )}
                        </div>
                        {vendor.contact_name && (
                          <div className="text-sm text-muted-foreground mb-2">
                            Contact: {vendor.contact_name}
                          </div>
                        )}
                        <div className="space-y-1 text-sm mb-3">
                          {vendor.phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="w-3 h-3" />
                              {vendor.phone}
                            </div>
                          )}
                          {vendor.email && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="w-3 h-3" />
                              {vendor.email}
                            </div>
                          )}
                          {vendor.website && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Globe className="w-3 h-3" />
                              <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="hover:text-neon-blue">
                                Website
                              </a>
                            </div>
                          )}
                        </div>
                        {vendor.rating && (
                          <div className="flex items-center gap-1 mb-2">
                            <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                            <span className="font-semibold">{vendor.rating}</span>
                          </div>
                        )}
                        {vendor.services && vendor.services.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {vendor.services.slice(0, 3).map((service, idx) => (
                              <span key={idx} className="text-xs bg-white/5 px-2 py-1 rounded">
                                {service}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Venues Tab - Similar structure, continuing in next part due to length */}
            <TabsContent value="venues">
              <div className="flex justify-between items-center mb-6">
                <p className="text-muted-foreground">Manage event venues and locations</p>
                <Dialog open={showVenueDialog} onOpenChange={(open) => { setShowVenueDialog(open); if (!open) resetVenueForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-neon-purple hover:bg-neon-purple/80 text-white" data-testid="create-venue-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Venue
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-venue-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl text-foreground">
                        {editingVenue ? 'Edit Venue' : 'Add New Venue'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Venue Name *</Label>
                          <Input
                            value={newVenue.name}
                            onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="venue-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Venue Type *</Label>
                          <Select value={newVenue.venue_type} onValueChange={(v) => setNewVenue({ ...newVenue, venue_type: v })}>
                            <SelectTrigger className="bg-background border-border text-foreground" data-testid="venue-type-select">
                              <SelectValue placeholder="Select venue type" />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {venueTypes.map((vt) => (
                                <SelectItem key={vt.id} value={vt.name}>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-3 h-3 rounded-full" 
                                      style={{ backgroundColor: vt.color }}
                                    />
                                    {vt.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Venue Image</Label>
                        <div className="flex items-start gap-4">
                          {venueImagePreview ? (
                            <div className="relative">
                              <div className="w-32 h-32 rounded-xl overflow-hidden border border-border bg-muted">
                                <img src={venueImagePreview} alt="Preview" className="w-full h-full object-cover" />
                              </div>
                              <button
                                type="button"
                                onClick={removeVenueImage}
                                className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-lg"
                                data-testid="remove-venue-image-btn"
                              >
                                <X className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          ) : (
                            <label className="w-32 h-32 rounded-xl border-2 border-dashed border-border bg-muted flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-muted/70 transition-all">
                              <ImagePlus className="w-8 h-8 text-muted-foreground mb-2" />
                              <span className="text-xs text-muted-foreground text-center px-2">Click to upload</span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleVenueImageUpload}
                                className="hidden"
                                data-testid="venue-image-input"
                              />
                            </label>
                          )}
                          <div className="flex-1 text-xs text-muted-foreground pt-2">
                            <p>Upload a photo of the venue.</p>
                            <p className="mt-1">Max size: 2MB</p>
                            <p className="mt-1">Recommended: Landscape format</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Address</Label>
                        <Input
                          value={newVenue.address}
                          onChange={(e) => setNewVenue({ ...newVenue, address: e.target.value })}
                          className="bg-background border-border text-foreground"
                          data-testid="venue-address-input"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">City</Label>
                          <Input
                            value={newVenue.city}
                            onChange={(e) => setNewVenue({ ...newVenue, city: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="venue-city-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">State</Label>
                          <Input
                            value={newVenue.state}
                            onChange={(e) => setNewVenue({ ...newVenue, state: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="venue-state-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">ZIP</Label>
                          <Input
                            value={newVenue.zip_code}
                            onChange={(e) => setNewVenue({ ...newVenue, zip_code: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="venue-zip-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Capacity</Label>
                          <Input
                            type="number"
                            value={newVenue.capacity}
                            onChange={(e) => setNewVenue({ ...newVenue, capacity: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="venue-capacity-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Contact Name</Label>
                          <Input
                            value={newVenue.contact_name}
                            onChange={(e) => setNewVenue({ ...newVenue, contact_name: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="venue-contact-name-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Contact Phone</Label>
                          <Input
                            value={newVenue.contact_phone}
                            onChange={(e) => setNewVenue({ ...newVenue, contact_phone: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="venue-contact-phone-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Contact Email</Label>
                          <Input
                            type="email"
                            value={newVenue.contact_email}
                            onChange={(e) => setNewVenue({ ...newVenue, contact_email: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="venue-contact-email-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Parking Info</Label>
                        <Input
                          value={newVenue.parking_info}
                          onChange={(e) => setNewVenue({ ...newVenue, parking_info: e.target.value })}
                          placeholder="e.g., Valet available, parking garage"
                          className="bg-background border-border text-foreground"
                          data-testid="venue-parking-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Setup Notes</Label>
                        <textarea
                          value={newVenue.setup_notes}
                          onChange={(e) => setNewVenue({ ...newVenue, setup_notes: e.target.value })}
                          placeholder="Equipment access, load-in details, etc."
                          className="w-full min-h-[60px] bg-background border border-border rounded-lg p-3 text-foreground"
                          data-testid="venue-setup-notes-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Restrictions</Label>
                        <Input
                          value={newVenue.restrictions}
                          onChange={(e) => setNewVenue({ ...newVenue, restrictions: e.target.value })}
                          placeholder="e.g., No open flames, noise curfew 11PM"
                          className="bg-background border-border text-foreground"
                          data-testid="venue-restrictions-input"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => { setShowVenueDialog(false); resetVenueForm(); }}
                          className="flex-1 border-border"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={editingVenue ? handleUpdateVenue : handleCreateVenue}
                          className="flex-1 bg-neon-purple hover:bg-neon-purple/80 text-white"
                          data-testid="venue-create-submit-btn"
                        >
                          {editingVenue ? 'Update Venue' : 'Add Venue'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Venue Search and Sort */}
              <div className="flex gap-2 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search venues..."
                    value={venueSearch}
                    onChange={(e) => setVenueSearch(e.target.value)}
                    className="pl-10 bg-card border-border"
                    data-testid="venue-search-input"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setVenueSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className="border-border flex items-center gap-2 whitespace-nowrap"
                  data-testid="sort-venues-btn"
                >
                  {venueSortOrder === 'asc' ? (
                    <ArrowUp className="w-4 h-4" />
                  ) : (
                    <ArrowDown className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Name {venueSortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
                </Button>
              </div>

              {venues.length === 0 ? (
                <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-venues-message">
                  <p className="text-muted-foreground">No venues yet. Add your first venue!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="venues-list">
                  {venues
                    .filter(v => v.name.toLowerCase().includes(venueSearch.toLowerCase()))
                    .sort((a, b) => {
                      const nameA = (a.name || '').toLowerCase();
                      const nameB = (b.name || '').toLowerCase();
                      return venueSortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
                    })
                    .map((venue) => (
                    <div
                      key={venue.id}
                      className="glass-card-hover rounded-2xl overflow-hidden relative"
                      data-testid={`venue-item-${venue.id}`}
                    >
                      {venue.image ? (
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={venue.image} 
                            alt={venue.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="h-32 bg-gradient-to-br from-neon-purple/20 to-neon-blue/10 flex items-center justify-center">
                          <MapPin className="w-12 h-12 text-neon-purple/50" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="absolute top-4 right-4 flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditVenue(venue)}
                            className="text-neon-blue hover:text-neon-blue/80 bg-black/50 backdrop-blur"
                            data-testid={`edit-venue-btn-${venue.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteVenue(venue.id)}
                            className="text-red-500 hover:text-red-400 bg-black/50 backdrop-blur"
                            data-testid={`delete-venue-btn-${venue.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="mb-3">
                          <span className="text-xs text-neon-purple font-semibold uppercase">{venue.venue_type}</span>
                          <h3 className="font-heading text-xl font-bold mt-1">{venue.name}</h3>
                        </div>
                        {(venue.address || venue.city) && (
                          <div className="text-sm text-muted-foreground mb-3">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {venue.address && <span>{venue.address}, </span>}
                            {venue.city && <span>{venue.city}</span>}
                            {venue.state && <span>, {venue.state}</span>}
                          </div>
                        )}
                        {venue.capacity && (
                          <div className="text-sm mb-2">
                            <span className="text-muted-foreground">Capacity:</span> <span className="font-semibold">{venue.capacity} guests</span>
                          </div>
                        )}
                        {venue.contact_name && (
                          <div className="text-sm text-muted-foreground">
                            Contact: {venue.contact_name}
                          </div>
                        )}
                        {venue.parking_info && (
                          <div className="text-xs text-muted-foreground mt-2">
                            Parking: {venue.parking_info}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
    </Layout>
  );
}
